import { useEffect, useState } from 'react'
import type { VendorExitRecord } from '../../lib/exit'
import { vendorLifecycle, type VendorLifecycle } from '../../lib/lifecycle'
import { personLabel } from '../../lib/people'
import { StageTrack, Status, type Stage, type StageState } from '../kit'
import { dayOf } from './facts'

// The vendor's life as a line with the vendor on it: Request, Due diligence,
// Approval, Active, Offboarding, Closed. The stages are named so that the
// vendor's stage word (lib/status.ts) is the stage it stands at: Requested, In
// due diligence, Awaiting approval, Active, Offboarding, Closed. "Draft" and
// "Signed out" were a second set of names for the same places.
//
// # THE REQUEST STAGE (RELEASE 3.3)
//
// The line started at Due diligence, so a vendor nobody had approved was drawn
// as already in due diligence (C2 of the 12 Oct plan). It starts at Request
// now: a DRAFT vendor whose request is not approved stands there, and one whose
// request is approved stands at Due diligence. Two approvals, two stages: the
// request at the start and the vendor at Approval (C3). A request rejected
// ends the line at Request, as a rejected vendor ends it at Approval.
//
// # WHERE SUSPENDED AND REJECTED GO
//
// Neither is a stop on the line. Suspended is a side branch off Active that
// leads back to Active or on to Offboarding, so the Active stage is drawn as
// blocked and a Suspended label sits under the track. Rejected ends the line at
// Approval, or at Request where the request itself was rejected, so that stage
// is drawn rejected and everything after it fades.
//
// # WHAT THE SUB-LINES MAY SAY
//
// Only what an API returned: the date the vendor entered its current status and
// who approved it, from the lifecycle read, and from the exit read how many
// sign-offs the chain holds and, while the exit is under way, which stage of
// how many is waiting and whose job it is. A stage with nothing recorded has no
// sub-line; a guessed date on a lifecycle is how an audit finds a false record.
//
// # ONE SET OF STAGES, DRAWN TWICE
//
// The record's one line (NowLine.tsx) shows the same journey as five dots and
// a sentence, and this track opens under it. Both are drawn from
// lifecycleStages, so the dots cannot stand somewhere the track does not.

const KEYS = ['request', 'draft', 'approved', 'active', 'offboarding', 'signed-out'] as const
const LABELS = ['Request', 'Due diligence', 'Approval', 'Active', 'Offboarding', 'Closed']

// Where a vendor stands on the line. requestApproved false puts a DRAFT or a
// REJECTED vendor at Request; absent (an API older than the field) reads as
// approved, the stage every vendor that API knew was past.
export function position(status: string, requestApproved?: boolean): number {
  const atRequest = requestApproved === false
  switch (status) {
    case 'DRAFT':
      return atRequest ? 0 : 1
    case 'REJECTED':
      return atRequest ? 0 : 2
    case 'PENDING_APPROVAL':
      return 2
    case 'ACTIVE':
    case 'SUSPENDED':
      return 3
    case 'OFFBOARDING':
      return 4
    case 'OFFBOARDED':
      return 5
    default:
      return 1
  }
}

export type LifecycleFacts = {
  status: string
  // The vendor's own field (release 3.3): false while nobody has approved the
  // request. Absent reads as approved.
  requestApproved?: boolean
  // The lifecycle read, or null while it loads or when it was refused: the
  // stages still draw from the status on the record; only the dates go.
  life: VendorLifecycle | null
  liveExit: VendorExitRecord | null
  signOffs: number | null
  // The stage an open exit waits on: signed so far, stages in the chain, and
  // the job of the stage marked next. Null when no exit is under way.
  exitStage?: { signed: number; total: number; job?: string } | null
  names: Record<string, string>
}

export function useLifecycle(vendorId: string, status: string): VendorLifecycle | null {
  const [life, setLife] = useState<VendorLifecycle | null>(null)
  useEffect(() => {
    let live = true
    vendorLifecycle(vendorId)
      .then((l) => {
        if (live) setLife(l)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [vendorId, status])
  return life
}

function sinceOf({ life, status }: LifecycleFacts): string | undefined {
  return life && life.status === status && life.statusSince ? 'since ' + dayOf(life.statusSince) : undefined
}

// Who approved the request, for the Request stage once it is done: a person by
// name, the importer as "at import". The upgrade and the seed approved nothing
// a person did, so they say nothing.
function requestSub(f: LifecycleFacts): string | undefined {
  const a = f.life?.request?.approval
  if (!a) return undefined
  if (a.basis === 'PERSON' && a.by) return 'by ' + personLabel(f.names, a.by)
  if (a.basis === 'IMPORT') return 'at import'
  return undefined
}

export function lifecycleStages(f: LifecycleFacts): Stage[] {
  const { status, life, liveExit, signOffs, exitStage, names } = f
  const at = position(status, f.requestApproved)
  const since = sinceOf(f)
  return KEYS.map((key, i) => {
    let state: StageState = i < at ? 'done' : i === at ? 'current' : 'upcoming'
    if (status === 'OFFBOARDED' && i === at) state = 'done'
    if (status === 'REJECTED' && i === at) state = 'rejected'
    if (status === 'SUSPENDED' && i === at) state = 'blocked'
    let sub: string | undefined
    if (i === at) sub = since
    if (key === 'request' && state === 'done') sub = requestSub(f)
    if (key === 'approved' && state === 'done' && life?.approvedBy) sub = 'by ' + personLabel(names, life.approvedBy)
    if (key === 'offboarding' && state === 'upcoming' && liveExit?.status === 'PROPOSED') sub = 'Proposed'
    if (key === 'signed-out' && state === 'upcoming' && signOffs) sub = signOffs + (signOffs === 1 ? ' sign-off' : ' sign-offs')
    let meta: string | undefined
    if (key === 'offboarding' && state === 'current' && exitStage && exitStage.total > 0 && exitStage.signed < exitStage.total) {
      meta = 'Stage ' + (exitStage.signed + 1) + ' of ' + exitStage.total + (exitStage.job ? ', ' + exitStage.job : '')
    }
    return { key, label: LABELS[i] ?? key, state, sub, meta }
  })
}

// Where the vendor stands, for the one line: the stage beside the five dots,
// what goes with it, and the name the dots carry as an image.
//
// Suspended and Rejected are said by name, because the stage they sit on
// (Active, Approval) is not where the vendor is. The date goes with the stage
// wherever the lifecycle read gave one, and who approved with an active or
// suspended vendor: "Suspended" without its date is half the fact, and both
// are short (review of 11 Oct 2026). An exit under way adds whose sign-off it
// waits on, which nothing else in the head says.
export function standing(f: LifecycleFacts): { stage: string; more: string[]; dots: string } {
  const at = position(f.status, f.requestApproved)
  const here = lifecycleStages(f)[at]
  const rejected = at === 0 ? 'Rejected at request' : 'Rejected at approval'
  const stage = f.status === 'SUSPENDED' ? 'Suspended' : f.status === 'REJECTED' ? rejected : (here?.label ?? '')
  const approved = (f.status === 'ACTIVE' || f.status === 'SUSPENDED') && f.life?.approvedBy
  const more = [
    sinceOf(f),
    here?.meta ? 'sign-off ' + here.meta.replace(/^Stage /, '') : undefined,
    approved ? 'approved by ' + personLabel(f.names, approved) : undefined,
  ].filter((fact): fact is string => Boolean(fact))
  return { stage, more, dots: 'Vendor lifecycle, stage ' + (at + 1) + ' of ' + KEYS.length }
}

export function LifecycleTrack(f: LifecycleFacts) {
  const since = sinceOf(f)
  return (
    <section className="vd-life" aria-label="Lifecycle">
      <StageTrack stages={lifecycleStages(f)} label="Vendor lifecycle" />
      {f.status === 'SUSPENDED' && (
        <p className="vd-branch">
          <Status tone="warn">Suspended</Status>
          {since && <span className="kit-mono">{since}</span>}
        </p>
      )}
    </section>
  )
}
